import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import {
  DSButton,
  DSCardWrapper,
} from "@components/design-system/molecules";
import {
  faCircleExclamation,
  faThumbsDown,
  faThumbsUp,
  faUsers,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAuth, useCurrentUser } from "@hooks";
import mixpanel from "mixpanel-browser";
import {
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { alert } from "../common/Alert";

const ACCEPT_TEAM_INVITATION = gql`
  mutation AcceptTeamInvitation($inviteUuid: String, $token: String) {
    acceptTeamInvitation(inviteUuid: $inviteUuid, token: $token) {
      ok
    }
  }
`;

const DENY_TEAM_INVITATION = gql`
  mutation DenyTeamInvitation($inviteUuid: String, $token: String) {
    denyTeamInvitation(inviteUuid: $inviteUuid, token: $token) {
      ok
    }
  }
`;

const UNAUTHENTICATED_TEAM_INVITE_QUERY = gql`
  query UnauthenticatedTeamInvite($accessToken: String!) {
    unauthenticatedTeamInvite(accessToken: $accessToken) {
      uuid
      inviteeEmail
      status
      teamName
    }
  }
`;

const GET_TEAM_INVITE = gql`
  query TeamInvite($uuid: String) {
    teamInvite(uuid: $uuid) {
      uuid
      inviteeEmail
      status
      teamName
    }
  }
`;

export default function ExistingUserAcceptTeamInvite() {
  const { inviteUuid } = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get("accessToken");
  const [acceptTeamInvitation] = useMutation(ACCEPT_TEAM_INVITATION);
  const [denyTeamInvitation] = useMutation(DENY_TEAM_INVITATION);
  const { isUserAuthenticated } = useAuth();
  const { refetch } = useCurrentUser();

  const {
    data: unauthenticatedTeamInviteData,
    loading: unauthenticatedLoading,
  } = useQuery(UNAUTHENTICATED_TEAM_INVITE_QUERY, {
    variables: { accessToken: accessToken },
    skip: !accessToken || isUserAuthenticated,
    fetchPolicy: "no-cache",
  });

  const {
    data: authenticatedTeamInviteData,
    loading: authenticatedLoading,
  } = useQuery(GET_TEAM_INVITE, {
    variables: { uuid: inviteUuid },
    skip: !isUserAuthenticated,
    fetchPolicy: "no-cache",
  });

  const loading = unauthenticatedLoading || authenticatedLoading;
  const data =
    authenticatedTeamInviteData?.teamInvite ||
    unauthenticatedTeamInviteData?.unauthenticatedTeamInvite;
  const isPending = data?.status === "pending";
  const navigate = useNavigate();

  async function acceptInvite() {
    try {
      const response = await acceptTeamInvitation({
        variables: {
          inviteUuid,
          token: accessToken,
        },
      });

      const { ok } = response.data.acceptTeamInvitation;

      if (ok) {
        alert(
          "success",
          `You have successfully joined the team${
            isUserAuthenticated ? "!" : " - now just sign in!"
          }`,
        );
        mixpanel.track("Accepted team invite", {
          inviteeEmail: data?.inviteeEmail,
          teamName: data?.teamName,
          inviteUuid,
        });
        if (isUserAuthenticated) {
          refetch();
        }
        navigate("/");
      }
    } catch (error) {
      alert("error", `Error accepting invite: ${error.message}`);
      mixpanel.track("Error accepting team invite", {
        error: error.message,
        inviteeEmail: data?.inviteeEmail,
        teamName: data?.teamName,
        inviteUuid,
      });
    }
  }

  async function rejectInvite() {
    try {
      const response = await denyTeamInvitation({
        variables: {
          inviteUuid,
          token: accessToken,
        },
      });

      const { ok } = response.data.denyTeamInvitation;
      if (ok) {
        mixpanel.track("Rejected team invite", {
          inviteeEmail: data?.inviteeEmail,
          teamName: data?.teamName,
          inviteUuid,
        });
        navigate("/");
      }
    } catch (error) {
      alert("error", `Error rejecting invite: ${error.message}`);
      mixpanel.track("Error rejecting team invite", {
        error: error.message,
        inviteeEmail: data?.inviteeEmail,
        teamName: data?.teamName,
        inviteUuid,
      });
    }
  }

  if (loading) return null;

  if (!isPending) {
    return (
      <div className="flex flex-col h-[600px] items-center justify-center my-10">
        <DSCardWrapper
          variant="default"
          padding="p-10"
          dataTestId="accept-team-invite-error"
        >
          <div className="flex flex-col items-center space-y-6 min-w-sm">
            <div className="w-16 h-16 bg-danger-subtle rounded-full flex items-center justify-center text-danger">
              <FontAwesomeIcon icon={faCircleExclamation} size="2x" />
            </div>
            <h2 className="text-center">
              This invite is no longer valid
            </h2>
            <p className="text-center text-secondary">
              This team invitation may have expired, been revoked, or
              already been used. Please ask your team admin to send a
              new invitation.
            </p>
            <DSButton
              variant="primary"
              size="md"
              onClick={() =>
                navigate(isUserAuthenticated ? "/" : "/auth/login")
              }
              dataTestId="invite-error-home-button"
            >
              {isUserAuthenticated ? "Go to Dashboard" : "Sign In"}
            </DSButton>
          </div>
        </DSCardWrapper>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[600px] items-center justify-center my-10">
      <DSCardWrapper
        variant="default"
        padding="p-10"
        dataTestId="accept-team-invite-card"
      >
        <div className="flex flex-col items-center space-y-10">
          <h2 className="text-center">Accept Team Invite?</h2>
          <h4>
            Your account ({data?.inviteeEmail}) has been invited to
            join this team on Scribenote:{" "}
          </h4>
          <DSCardWrapper
            variant="info"
            padding="p-md"
            dataTestId="team-name-card"
          >
            <div className="flex flex-col items-center justify-center">
              <div className="flex flex-row items-center space-x-sm">
                <div className="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white">
                  <FontAwesomeIcon icon={faUsers} size="1x" />
                </div>
                <h4>{data?.teamName}</h4>
              </div>
            </div>
          </DSCardWrapper>
          <h5 className="max-w-2xl text-center text-secondary">
            Accepting means other members of this team will have
            access to your Scribenote data and you&apos;ll have access
            to theirs. Be sure you received this invitation from
            someone you trust. If you have any doubts, please contact
            the person who sent you this invitation or feel free to
            reach out to Scribenote support.
          </h5>
          <div className="flex flex-row items-center space-x-sm">
            <DSButton
              variant="secondary"
              size="md"
              icon={faThumbsDown}
              onClick={() => {
                rejectInvite();
              }}
              dataTestId="reject-invite-button"
            >
              Reject Invite
            </DSButton>
            <DSButton
              variant="primary"
              size="md"
              icon={faThumbsUp}
              onClick={() => {
                acceptInvite();
              }}
              dataTestId="accept-invite-button"
            >
              Accept Invite
            </DSButton>
          </div>
        </div>
      </DSCardWrapper>
    </div>
  );
}
